import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import './ProductVideoPage.css';

const DEFAULT_CONFIG = {
  fields: { title: false, fabric: true, quality: true, rate: false, type: false, width: true },
  custom: '',
  music: true,
  lookbook: 'off',
  logo: { show: true, corner: 'tr', size: 0.22, opacity: 1, shadow: 0.45 },
};

const DEFAULT_PRESET_NAME = 'Reel';
// The video-render Cloud Run service (ffmpeg + Veo), called DIRECTLY for anything
// that can run long — Firebase Hosting's rewrite proxy enforces a 60s timeout,
// but renders/Veo generations take 1-4 minutes. NOT the same as the `app`
// Functions service (which has no ffmpeg installed).
const LONG_VIDEO_API_BASE = 'https://video-render-frgo5nxgza-uc.a.run.app';

const LOOKBOOK_OPTIONS = [
  ['off', 'Off — product images only'],
  ['editorial', 'Editorial only — fabric shots'],
  ['all', 'All — includes styled garments'],
];

const FIELD_LABELS = [
  ['title', 'Title'],
  ['fabric', 'Fabric'],
  ['quality', 'Quality'],
  ['rate', 'Rate'],
  ['type', 'Type'],
  ['width', 'Width'],
];

const LIST_FILTERS = [
  ['all', 'All'],
  ['pending', 'Pending'],
  ['approved', 'Approved'],
  ['published', 'Published'],
  ['skipped', 'Skipped'],
];

function loadConfig() {
  try {
    const saved = JSON.parse(localStorage.getItem('pv_config_v2') || 'null');
    if (saved) return mergeConfig(saved);
  } catch (_) { /* ignore */ }
  return DEFAULT_CONFIG;
}

function mergeConfig(config = {}) {
  return {
    ...DEFAULT_CONFIG,
    ...config,
    fields: { ...DEFAULT_CONFIG.fields, ...(config.fields || {}) },
    logo: { ...DEFAULT_CONFIG.logo, ...(config.logo || {}) },
  };
}

function statusGroup(item) {
  const status = String(item?.shopifyStatus || '').toUpperCase();
  if (status === 'PUBLISHED') return 'published';
  if (status === 'READY') return 'approved';
  if (status === 'SKIPPED') return 'skipped';
  return 'pending';
}

function apiUrl(endpoint, { longRunning = false } = {}) {
  if (!longRunning) return endpoint;
  if (typeof window === 'undefined') return endpoint;
  const host = String(window.location.hostname || '');
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  return isLocal ? endpoint : `${LONG_VIDEO_API_BASE}${endpoint}`;
}

async function parseApiResponse(res, fallbackMessage) {
  const text = await res.text();
  let data = null;
  try { data = JSON.parse(text); } catch (_) { /* non-JSON response */ }
  if (!res.ok || !data || !data.success) {
    const detail = (data && data.message) || `HTTP ${res.status} ${res.statusText} — ${text.slice(0, 300)}`;
    throw new Error(detail || fallbackMessage);
  }
  return data;
}

function ProductVideoPage() {
  const { currentUser } = useAuth();
  const [mode, setMode] = useState('sku');
  const [input, setInput] = useState('');
  const [config, setConfig] = useState(loadConfig);

  const [previewBusy, setPreviewBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null); // { image, resolved, logoAvailable }
  const [video, setVideo] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushed, setPushed] = useState(null);
  const [presetName, setPresetName] = useState('');
  const [presetMsg, setPresetMsg] = useState('');
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(() => localStorage.getItem('pv_preset_name') || DEFAULT_PRESET_NAME);
  const [presetsBusy, setPresetsBusy] = useState(false);
  const [list, setList] = useState(null); // { total, items }
  const [listBusy, setListBusy] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [extendingId, setExtendingId] = useState(null);
  const [veoTier, setVeoTier] = useState('fast');   // 'fast' | 'lite'
  const [veoMode, setVeoMode] = useState('reveal'); // 'reveal' | 'scale_reveal'
  const [veoEthnicity, setVeoEthnicity] = useState(''); // model appearance
  const [veoBusy, setVeoBusy] = useState(false);
  const [veoResult, setVeoResult] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [skippingId, setSkippingId] = useState(null);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [listFilter, setListFilter] = useState('all');
  const [lookbookImages, setLookbookImages] = useState([]); // [{id, url, code}] for 'outfit_flow' picker
  const [lookbookImagesBusy, setLookbookImagesBusy] = useState(false);
  const [lookbookImagesError, setLookbookImagesError] = useState('');
  const [selectedLookbookUrl, setSelectedLookbookUrl] = useState('');

  // remember last selection
  useEffect(() => { localStorage.setItem('pv_config_v2', JSON.stringify(config)); }, [config]);
  useEffect(() => { localStorage.setItem('pv_preset_name', selectedPreset); }, [selectedPreset]);

  const setField = (k, v) => setConfig(c => ({ ...c, fields: { ...c.fields, [k]: v } }));
  const setLogo = (k, v) => setConfig(c => ({ ...c, logo: { ...c.logo, [k]: v } }));

  const applyPreset = useCallback((preset) => {
    if (!preset) return;
    setConfig(mergeConfig(preset.config || {}));
    setSelectedPreset(preset.name);
    setPresetMsg(`Using preset "${preset.name}".`);
  }, []);

  const loadPresets = useCallback(async ({ applyDefault = false } = {}) => {
    if (!currentUser) return;
    setPresetsBusy(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/product-video/presets', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load presets.');
      const items = Array.isArray(data.items)
        ? data.items
        : (Array.isArray(data.presets) ? data.presets.map(name => ({ name, config: {} })) : []);
      setPresets(items);
      if (applyDefault) {
        const wanted = items.find(p => p.name === selectedPreset)
          || items.find(p => p.name === DEFAULT_PRESET_NAME);
        if (wanted) applyPreset(wanted);
      }
    } catch (e) {
      setPresetMsg(e.message);
    } finally {
      setPresetsBusy(false);
    }
  }, [applyPreset, currentUser, selectedPreset]);

  useEffect(() => { loadPresets({ applyDefault: true }); }, [loadPresets]);

  const call = async (endpoint, extraBody = {}, options = {}) => {
    const val = input.trim();
    if (!val) throw new Error('Enter a SKU or Product ID.');
    const token = await currentUser.getIdToken();
    const idPart = mode === 'sku' ? { sku: val } : { productId: val };
    const res = await fetch(apiUrl(endpoint, options), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...idPart, config, ...extraBody }),
    });
    return parseApiResponse(res, 'Request failed.');
  };

  const doPreview = async () => {
    if (previewBusy) return;
    setPreviewBusy(true); setError(''); setVideo(null);
    try { setPreview(await call('/api/v1/product-video/preview')); }
    catch (e) { setError(e.message); }
    finally { setPreviewBusy(false); }
  };

  const doGenerate = async () => {
    if (genBusy) return;
    setGenBusy(true); setError(''); setPushed(null);
    try {
      const data = await call('/api/v1/product-video/generate', { deliver: 'library' }, { longRunning: true });
      setVideo(data);
      await loadList();
    }
    catch (e) { setError(e.message); }
    finally { setGenBusy(false); }
  };

  const doVeo = async () => {
    const val = input.trim();
    if (!val || veoBusy) return;
    if (veoMode === 'outfit_flow' && !selectedLookbookUrl) return;
    setVeoBusy(true); setError(''); setVeoResult(null);
    try {
      const token = await currentUser.getIdToken();
      const idPart = mode === 'sku' ? { sku: val } : { productId: val };
      const res = await fetch(apiUrl('/api/v1/product-video/veo', { longRunning: true }), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...idPart, mode: veoMode,
          tier: (veoMode === 'scale_reveal' || veoMode === 'auto' || veoMode === 'outfit_flow') ? 'fast' : veoTier,
          durationSeconds: 8, ethnicity: veoEthnicity,
          lookbookImageUrl: veoMode === 'outfit_flow' ? selectedLookbookUrl : '',
        }),
      });
      const data = await parseApiResponse(res, 'Veo generation failed.');
      setVeoResult(data);
      await loadList();
    } catch (e) { setError(e.message); }
    finally { setVeoBusy(false); }
  };

  const loadLookbookImages = async () => {
    const val = input.trim();
    if (!val || lookbookImagesBusy) return;
    setLookbookImagesBusy(true); setLookbookImagesError(''); setLookbookImages([]); setSelectedLookbookUrl('');
    try {
      const token = await currentUser.getIdToken();
      const idPart = mode === 'sku' ? { sku: val } : { productId: val };
      const qs = new URLSearchParams(idPart).toString();
      const res = await fetch(apiUrl(`/api/v1/product-video/lookbook-images?${qs}`, { longRunning: true }), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await parseApiResponse(res, 'Failed to load Lookbook images.');
      setLookbookImages(data.images || []);
    } catch (e) { setLookbookImagesError(e.message); }
    finally { setLookbookImagesBusy(false); }
  };

  const loadList = async () => {
    if (listBusy) return;
    setListBusy(true); setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/product-video/list?limit=200', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load list.');
      setList(data);
    } catch (e) { setError(e.message); }
    finally { setListBusy(false); }
  };

  const approveRow = async (productId) => {
    if (approvingId) return;
    setApprovingId(productId); setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(apiUrl('/api/v1/product-video/approve', { longRunning: true }), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ productId }),
      });
      await parseApiResponse(res, 'Approve failed.');
      await loadList();
    } catch (e) { setError(e.message); }
    finally { setApprovingId(null); }
  };

  const extendRow = async (productId) => {
    if (extendingId) return;
    setExtendingId(productId); setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(apiUrl('/api/v1/product-video/extend', { longRunning: true }), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ productId }),
      });
      await parseApiResponse(res, 'Extend failed.');
      await loadList();
    } catch (e) { setError(e.message); }
    finally { setExtendingId(null); }
  };

  const regenerateRow = async (productId) => {
    if (regeneratingId) return;
    setRegeneratingId(productId); setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(apiUrl('/api/v1/product-video/regenerate', { longRunning: true }), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ productId, config }),
      });
      await parseApiResponse(res, 'Regenerate failed.');
      await loadList();
    } catch (e) { setError(e.message); }
    finally { setRegeneratingId(null); }
  };

  const skipRow = async (productId) => {
    if (skippingId) return;
    setSkippingId(productId); setError('');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/product-video/skip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ productId }),
      });
      await parseApiResponse(res, 'Skip failed.');
      await loadList();
    } catch (e) { setError(e.message); }
    finally { setSkippingId(null); }
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { setPresetMsg('Enter a preset name.'); return; }
    setPresetMsg('Saving…');
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/v1/product-video/presets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, config }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to save.');
      setPresetMsg(`Saved. Use in bulk: --preset "${name}"`);
      setSelectedPreset(name);
      await loadPresets();
    } catch (e) { setPresetMsg(e.message); }
  };

  const doPush = async () => {
    if (pushBusy) return;
    setPushBusy(true); setError('');
    try { setPushed(await call('/api/v1/product-video/push', {}, { longRunning: true })); }
    catch (e) { setError(e.message); }
    finally { setPushBusy(false); }
  };

  const listCounts = useMemo(() => {
    const counts = { all: 0, pending: 0, approved: 0, published: 0, skipped: 0 };
    (list?.items || []).forEach((item) => {
      counts.all++;
      counts[statusGroup(item)]++;
    });
    return counts;
  }, [list]);

  const filteredItems = useMemo(() => {
    const items = list?.items || [];
    if (listFilter === 'all') return items;
    return items.filter((item) => statusGroup(item) === listFilter);
  }, [list, listFilter]);

  const playInlineVideo = (event, productId) => {
    setActiveVideoId(productId);
    const el = event.currentTarget;
    if (el && typeof el.play === 'function') el.play().catch(() => {});
  };

  return (
    <div className="pv-page">
      <h1>Product Video</h1>
      <p className="pv-sub">
        Build a 9:16 reel from a product's images with a configurable overlay (logo + text fields).
        Hit <strong>Preview</strong> to see the exact frame, then <strong>Generate</strong>. Needs the backend running locally (ffmpeg).
      </p>

      <div className="pv-layout">
        <div className="pv-controls">
          <div className="pv-mode">
            <label><input type="radio" checked={mode === 'sku'} onChange={() => setMode('sku')} /> SKU</label>
            <label><input type="radio" checked={mode === 'productId'} onChange={() => setMode('productId')} /> Product ID</label>
          </div>
          <input
            className="pv-input"
            placeholder={mode === 'sku' ? 'e.g. KD-0-TA062972-111' : 'e.g. 5460558315687'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doPreview()}
          />

          <fieldset className="pv-group">
            <legend>Logo</legend>
            <label className="pv-check">
              <input type="checkbox" checked={config.logo.show} onChange={(e) => setLogo('show', e.target.checked)} /> Show logo
            </label>
            {preview && !preview.logoAvailable && config.logo.show && (
              <div className="pv-hint">No logo file found — drop a transparent PNG at <code>backend/assets/logo.png</code>.</div>
            )}
            <div className="pv-row">
              <span>Corner</span>
              <label><input type="radio" checked={config.logo.corner === 'tl'} onChange={() => setLogo('corner', 'tl')} /> Top-left</label>
              <label><input type="radio" checked={config.logo.corner === 'tr'} onChange={() => setLogo('corner', 'tr')} /> Top-right</label>
            </div>
            <div className="pv-row">
              <span>Size</span>
              <input type="range" min="0.1" max="0.4" step="0.01" value={config.logo.size} onChange={(e) => setLogo('size', parseFloat(e.target.value))} />
              <em>{Math.round(config.logo.size * 100)}%</em>
            </div>
            <div className="pv-row">
              <span>Opacity</span>
              <input type="range" min="0.1" max="1" step="0.05" value={config.logo.opacity} onChange={(e) => setLogo('opacity', parseFloat(e.target.value))} />
              <em>{Math.round(config.logo.opacity * 100)}%</em>
            </div>
            <div className="pv-row">
              <span>Shadow</span>
              <input type="range" min="0" max="1" step="0.05" value={config.logo.shadow} onChange={(e) => setLogo('shadow', parseFloat(e.target.value))} />
              <em>{Math.round((config.logo.shadow ?? 0) * 100)}%</em>
            </div>
          </fieldset>

          <fieldset className="pv-group">
            <legend>Bottom text</legend>
            <div className="pv-fields">
              {FIELD_LABELS.map(([k, label]) => (
                <label key={k} className="pv-check">
                  <input type="checkbox" checked={!!config.fields[k]} onChange={(e) => setField(k, e.target.checked)} /> {label}
                </label>
              ))}
            </div>
            <input
              className="pv-input"
              placeholder="Custom text (optional) — e.g. New Arrival / DM to order"
              value={config.custom}
              onChange={(e) => setConfig(c => ({ ...c, custom: e.target.value }))}
            />
          </fieldset>

          <fieldset className="pv-group">
            <legend>Lookbook images</legend>
            {LOOKBOOK_OPTIONS.map(([val, label]) => (
              <label key={val} className="pv-check">
                <input
                  type="radio"
                  checked={config.lookbook === val}
                  onChange={() => setConfig(c => ({ ...c, lookbook: val }))}
                /> {label}
              </label>
            ))}
            <div className="pv-hint2">Real fabric images always play first; lookbook shots follow as styling inspiration.</div>
          </fieldset>

          <label className="pv-check">
            <input type="checkbox" checked={config.music} onChange={(e) => setConfig(c => ({ ...c, music: e.target.checked }))} /> Add music
          </label>

          <fieldset className="pv-group pv-veo">
            <legend>AI video (Veo) — experimental</legend>
            <div className="pv-row">
              <span>Style</span>
              <label><input type="radio" checked={veoMode === 'reveal'} onChange={() => setVeoMode('reveal')} /> Fabric→model reveal</label>
              <label><input type="radio" checked={veoMode === 'scale_reveal'} onChange={() => { setVeoMode('scale_reveal'); setVeoTier('fast'); }} /> Scale-aware reveal (3 photos + ruler if available)</label>
              <label><input type="radio" checked={veoMode === 'editorial'} onChange={() => setVeoMode('editorial')} /> Editorial motion (Lookbook V2 FABRIC_EDITORIAL only)</label>
              <label><input type="radio" checked={veoMode === 'auto'} onChange={() => { setVeoMode('auto'); setVeoTier('fast'); }} /> Auto (rotate style — for ad/social variety)</label>
              <label><input type="radio" checked={veoMode === 'outfit_flow'} onChange={() => { setVeoMode('outfit_flow'); setVeoTier('fast'); loadLookbookImages(); }} /> Flowing outfit (pick a Lookbook photo — model steady, fabric flows)</label>
            </div>
            <div className="pv-row">
              <span>Quality</span>
              <label><input type="radio" checked={veoTier === 'fast'} onChange={() => setVeoTier('fast')} /> Fast (~$0.80)</label>
              <label>
                <input type="radio" checked={veoTier === 'lite'} disabled={veoMode === 'scale_reveal' || veoMode === 'auto' || veoMode === 'outfit_flow'} onChange={() => setVeoTier('lite')} /> Lite (~$0.40){(veoMode === 'scale_reveal' || veoMode === 'auto' || veoMode === 'outfit_flow') ? ' — unavailable for this style' : ''}
              </label>
            </div>
            {veoMode === 'outfit_flow' && (
              <div className="pv-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                <span>Pick a Lookbook photo</span>
                {lookbookImagesBusy && <div className="pv-hint2">Loading Lookbook images…</div>}
                {lookbookImagesError && <div className="pv-hint2" style={{ color: '#b00' }}>{lookbookImagesError}</div>}
                {!lookbookImagesBusy && !lookbookImagesError && lookbookImages.length === 0 && (
                  <div className="pv-hint2">
                    No approved garment-style Lookbook image found for this SKU.
                    <button type="button" className="pv-btn" style={{ marginLeft: 8 }} onClick={loadLookbookImages} disabled={!input.trim()}>Retry</button>
                  </div>
                )}
                {lookbookImages.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {lookbookImages.map((img) => (
                      <button
                        type="button"
                        key={img.id}
                        onClick={() => setSelectedLookbookUrl(img.url)}
                        style={{
                          padding: 0, border: selectedLookbookUrl === img.url ? '3px solid #5b3df0' : '2px solid #ddd',
                          borderRadius: 6, cursor: 'pointer', background: 'none', overflow: 'hidden', width: 90,
                        }}
                        title={img.code}
                      >
                        <img src={img.url} alt={img.code} style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }} />
                        <div style={{ fontSize: 10, padding: '2px 4px', background: '#f2f2f2' }}>{img.code}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {veoMode !== 'editorial' && veoMode !== 'outfit_flow' && (
              <div className="pv-row">
                <span>Model</span>
                <select className="pv-select" value={veoEthnicity} onChange={(e) => setVeoEthnicity(e.target.value)}>
                  <option value="">Let AI choose</option>
                  <option value="indian">Indian</option>
                  <option value="east_asian">East Asian</option>
                  <option value="white">White</option>
                  <option value="latina">Latina</option>
                  <option value="middle_eastern">Middle Eastern</option>
                </select>
              </div>
            )}
            <div className="pv-hint2">
              {veoMode === 'scale_reveal'
                ? 'Uses the 1st, 2nd and 4th product photos (the 4th is usually the scale/ruler shot) as print references and asks Veo to preserve the true motif size. No guaranteed literal swatch opening frame like the reveal style — experimental. 8s → lands in the review list below (not live until you approve).'
                : veoMode === 'editorial'
                ? 'Animates the approved FABRIC_EDITORIAL shot from Lookbook V2 (already scale-correct) — no model, no outfit, just a gentle camera motion on the same fabric shot. Fails with a clear error if this product has no approved FABRIC_EDITORIAL image yet — generate + approve one in Lookbook V2 first. 8s → lands in the review list below (not live until you approve).'
                : veoMode === 'auto'
                ? "Randomly picks one of the available styles above each time you generate (skips Editorial if this product has no approved FABRIC_EDITORIAL, skips Scale-aware if it has under 2 photos) — use this when generating several clips for the same product so ads/social posts don't all look identical. The list below shows which style was actually used. 8s → lands in the review list below (not live until you approve)."
                : veoMode === 'outfit_flow'
                ? "Pick one of this product's approved garment-style Lookbook photos above. The model stays completely still — only the outfit's fabric flows, at a steady medium intensity (wind-blown, cascading, or rippling — the exact style varies each generation). Extend continues this same style. No invented scene, no billboard. 8s → lands in the review list below (not live until you approve)."
                : 'Starts on the real fabric photo, zooms out to reveal an AI model wearing an outfit made from it. Aspirational hero/ad shot — not a literal product. 8s → lands in the review list below (not live until you approve).'}
            </div>
            <button className="pv-btn pv-veobtn" onClick={doVeo} disabled={veoBusy || !input.trim() || (veoMode === 'outfit_flow' && !selectedLookbookUrl)}>
              {veoBusy ? 'Generating AI clip… (~2–3 min)' : 'Generate AI clip'}
            </button>
            {veoResult && (
              <div className="pv-pushed">
                ✓ AI {veoResult.mode} clip ({veoResult.tier}) in the library for review.
                {veoResult.shopifyVideoUrl && <div className="pv-url"><a href={veoResult.shopifyVideoUrl} target="_blank" rel="noreferrer">Watch clip</a></div>}
              </div>
            )}
          </fieldset>

          <fieldset className="pv-group">
            <legend>Preset</legend>
            <div className="pv-preset-list">
              {presets.length ? presets.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  className={`pv-preset-chip${selectedPreset === preset.name ? ' active' : ''}`}
                  onClick={() => applyPreset(preset)}
                >
                  {preset.name}
                </button>
              )) : (
                <span className="pv-hint2">{presetsBusy ? 'Loading presets…' : 'No saved presets yet.'}</span>
              )}
            </div>
            <div className="pv-preset">
              <input
                className="pv-input"
                placeholder='Preset name (e.g. "Reel")'
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
              />
              <button className="pv-btn ghost" type="button" onClick={savePreset}>Save current settings</button>
            </div>
            {presetMsg && <div className="pv-hint2">{presetMsg}</div>}
            <div className="pv-hint2">Saves logo + fields + lookbook + music so a batch run matches this exact look:
              <code>node scripts/videoBulk.js --collection "…" --preset NAME --push</code></div>
          </fieldset>

          <div className="pv-actions">
            <button className="pv-btn ghost" onClick={doPreview} disabled={previewBusy || !input.trim()}>
              {previewBusy ? 'Rendering…' : 'Preview'}
            </button>
            <button className="pv-btn" onClick={doGenerate} disabled={genBusy || !input.trim()}>
              {genBusy ? 'Generating… (~10–20s)' : 'Generate Video'}
            </button>
          </div>

          {error && <div className="pv-error">{error}</div>}
        </div>

        <div className="pv-stage">
          {video ? (
            <>
              <div className="pv-meta"><strong>{video.title}</strong> · {video.sku}</div>
              <video className="pv-frame" src={video.videoUrl} controls autoPlay loop playsInline />
              <div className="pv-url"><a href={video.videoUrl} target="_blank" rel="noreferrer">Open video URL</a></div>
              {video.delivered === 'library' ? (
                <button className="pv-btn pv-push" onClick={() => approveRow(video.productId)} disabled={approvingId === video.productId}>
                  {approvingId === video.productId ? 'Publishing…' : 'Approve & publish'}
                </button>
              ) : (
                <button className="pv-btn pv-push" onClick={doPush} disabled={pushBusy}>
                  {pushBusy ? 'Pushing to Shopify… (~15–60s)' : 'Push to Shopify'}
                </button>
              )}
              {video.delivered === 'library' && (
                <div className="pv-pushed">Generated in Shopify Files for review. It is not live until approved.</div>
              )}
              {pushed && (
                <div className="pv-pushed">
                  {pushed.status === 'READY' ? '✓ Live on the Shopify product gallery' : `Uploaded — Shopify is still processing (${pushed.status})`}
                  {pushed.shopifyVideoUrl && (
                    <div className="pv-url"><a href={pushed.shopifyVideoUrl} target="_blank" rel="noreferrer">Shopify video URL</a></div>
                  )}
                </div>
              )}
            </>
          ) : preview ? (
            <>
              <div className="pv-badge">
                Preview (still frame — video will animate)
                <br />
                {preview.imageCount} images ({preview.productImageCount} product
                {preview.lookbookCount > 0 ? ` + ${preview.lookbookCount} lookbook` : ''}) · ~{preview.estimatedSeconds}s
              </div>
              <img className="pv-frame" src={preview.image} alt="overlay preview" />
            </>
          ) : (
            <div className="pv-empty">Enter a SKU and hit <strong>Preview</strong>.</div>
          )}
        </div>
      </div>

      <div className="pv-listwrap">
        <div className="pv-listhead">
          <h2>Generated videos{list?.total != null ? ` (${list.total})` : ''}</h2>
          <button className="pv-btn ghost" onClick={loadList} disabled={listBusy}>
            {listBusy ? 'Loading…' : (list ? 'Refresh' : 'Show list')}
          </button>
        </div>
        {list && (
          <div className="pv-filterbar" role="tablist" aria-label="Video status filters">
            {LIST_FILTERS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`pv-filter-pill${listFilter === key ? ' active' : ''}`}
                onClick={() => setListFilter(key)}
              >
                {label} <span>{listCounts[key] || 0}</span>
              </button>
            ))}
          </div>
        )}
        {list && (
          filteredItems.length === 0 ? (
            <div className="pv-hint2">No videos generated yet.</div>
          ) : (
            <div className="pv-cardgrid">
              {filteredItems.map((it) => {
                const videoUrl = it.firebaseUrl || it.shopifyUrl;
                const status = String(it.shopifyStatus || '').toUpperCase();
                const isBusy = approvingId === it.productId || regeneratingId === it.productId || skippingId === it.productId || extendingId === it.productId;
                return (
                  <article className="pv-videocard" key={it.productId}>
                    <div className="pv-cardmedia">
                      {videoUrl ? (
                        <video
                          className={`pv-cardvideo${activeVideoId === it.productId ? ' playing' : ''}`}
                          src={videoUrl}
                          muted
                          playsInline
                          preload="metadata"
                          controls={activeVideoId === it.productId}
                          onClick={(event) => playInlineVideo(event, it.productId)}
                        />
                      ) : (
                        <div className="pv-cardempty">No video</div>
                      )}
                    </div>
                    <div className="pv-cardbody">
                      <div className="pv-cardtop">
                        <span className={`pv-status ${statusGroup(it)}`}>{status ? status.toLowerCase() : 'pending'}</span>
                        {it.aiVideo && <span className="pv-aibadge">AI · {it.aiVideo.durationSeconds}s</span>}
                        {it.productUrl ? <a className="pv-productlink" href={it.productUrl} target="_blank" rel="noreferrer">Open product</a> : null}
                      </div>
                      <div className="pv-cardsku">{it.sku}</div>
                      <h3 className="pv-cardtitle">{it.title}</h3>
                      <div className="pv-cardactions">
                        {status === 'REVIEW' && (
                          <button className="pv-approvebtn" onClick={() => approveRow(it.productId)} disabled={isBusy}>
                            {approvingId === it.productId ? 'Publishing…' : 'Approve & publish'}
                          </button>
                        )}
                        {status === 'REVIEW' && it.aiVideo && (
                          <button className="pv-smallbtn" onClick={() => extendRow(it.productId)} disabled={isBusy} title="Extend by ~7s of continued motion (optional, extra Veo cost)">
                            {extendingId === it.productId ? 'Extending…' : `Extend +7s (${it.aiVideo.durationSeconds}s→${it.aiVideo.durationSeconds + 7}s)`}
                          </button>
                        )}
                        <button className="pv-smallbtn" onClick={() => regenerateRow(it.productId)} disabled={isBusy}>
                          {regeneratingId === it.productId ? 'Regenerating…' : 'Regenerate'}
                        </button>
                        {status !== 'SKIPPED' && (
                          <button className="pv-smallbtn muted" onClick={() => skipRow(it.productId)} disabled={isBusy}>
                            {skippingId === it.productId ? 'Skipping…' : 'Skip'}
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )
        )}
        {list?.nextBefore && <div className="pv-hint2">Showing first {list.count}. Increase limit for more.</div>}
      </div>
    </div>
  );
}

export default ProductVideoPage;
